/**
 * Open external URLs in a predictable way:
 * - Reuse/focus: same `window.open` *name* reuses that browsing context if it is still open (browser-dependent).
 * - New tab: always `_blank`.
 *
 * Apps cannot scan “all tabs” for a URL; named targets are the supported pattern.
 */

export function normalizeExternalUrl(raw) {
  const trimmed = (raw || '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/** Stable window name per link id (keep reasonably short). */
export function externalLinkWindowName(linkId) {
  return `shifty_ext_${String(linkId || '').replace(/[^a-zA-Z0-9_-]/g, '_')}`;
}

/**
 * Open URL in a named window/tab: if that name is still open, the browser focuses it and navigates to href.
 * @returns {Window|null}
 */
export function openExternalLinkReuseOrFocus(linkId, rawUrl) {
  const href = normalizeExternalUrl(rawUrl);
  if (!href) return null;
  try {
    const w = window.open(href, externalLinkWindowName(linkId));
    if (w && typeof w.focus === 'function') {
      w.focus();
    }
    return w;
  } catch (e) {
    console.error('openExternalLinkReuseOrFocus:', e);
    return null;
  }
}

export function openExternalLinkNewTab(rawUrl) {
  const href = normalizeExternalUrl(rawUrl);
  if (!href) return;
  try {
    window.open(href, '_blank', 'noopener,noreferrer');
  } catch (e) {
    console.error('openExternalLinkNewTab:', e);
  }
}
